import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import {
  ArrowLeft,
  Save,
  User,
  ShieldCheck,
  Loader2,
  Phone,
  Mail,
  Lock,
  AlertCircle,
  Calendar,
  Search,
  ChevronDown,
  Check,
  Store,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import useUser from "../../../hooks/useUser";
import { sellerService } from "../../../services/sellerService";
import AdminLayout from "../Layout/AdminLayout";

/* ── Shared Form Components (defined outside to avoid re-mount on every render) ── */

function FormCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#232F3E] flex items-center justify-center">
            <Icon size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800">{title}</h3>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function FormInput({ icon: Icon, label, error, required, disabled = false, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={16} />
          </div>
        )}
        <input
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-4 py-2.5 border rounded-xl text-sm transition-all duration-200 outline-none ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : "border-slate-200 focus:border-[#f08804] focus:ring-2 focus:ring-[#f08804]/20"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        />
      </div>
      {error && (
        <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

function FormSelect({ icon: Icon, label, error, required, disabled = false, options = [], ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
            <Icon size={16} />
          </div>
        )}
        <select
          {...props}
          disabled={disabled}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-8 py-2.5 border rounded-xl text-sm transition-all duration-200 outline-none appearance-none ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : "border-slate-200 focus:border-[#f08804] focus:ring-2 focus:ring-[#f08804]/20"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : "bg-white"}`}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      {error && (
        <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}

function SearchableSelect({ icon: Icon, label, error, required, disabled, placeholder, options = [], value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((o) => o.value === value);

  useEffect(() => {
    const handler = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) { setSearch(""); return; }
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < 220;
    setDropdownStyle({
      position: "fixed",
      left: rect.left,
      width: rect.width,
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 9999,
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={containerRef}>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10">
            <Icon size={16} />
          </div>
        )}
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={() => setOpen(!open)}
          className={`w-full ${Icon ? "pl-10" : "pl-4"} pr-10 py-2.5 border rounded-xl text-sm text-left transition-all duration-200 outline-none ${
            error
              ? "border-rose-300 bg-rose-50 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20"
              : open
                ? "border-[#f08804] ring-2 ring-[#f08804]/20 bg-white"
                : "border-slate-200 bg-white hover:border-slate-300"
          } ${disabled ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}`}
        >
          <span className={selectedOption ? "text-slate-900" : "text-slate-400"}>
            {selectedOption ? selectedOption.label : placeholder || "Select..."}
          </span>
        </button>
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {error && (
        <p className="text-xs text-rose-500 mt-1.5 flex items-center gap-1">
          <AlertCircle size={12} /> {error}
        </p>
      )}

      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={dropdownStyle} className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100">
            <Search size={14} className="text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder-slate-400"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-400 text-center">No results found</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                    o.value === value
                      ? "bg-[#FFF4E6] text-[#C45500] font-medium"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.value === value && <Check size={14} className="text-[#C45500] flex-shrink-0" />}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── Main Component ── */

export default function UserAddEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const isEdit = location.pathname.includes("edit");
  const isAdd = !isEdit;

  const {
    fetchUserById,
    createUser,
    updateUser,
    fetchRoles,
    roles,
    rolesLoading,
  } = useUser();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [sellers, setSellers] = useState([]);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellerSearch, setSellerSearch] = useState("");

  const [form, setForm] = useState({
    username: "",
    password: "",
    userType: "MANAGEMENT",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "MALE",
    dateOfBirth: "",
    roleId: "",
    sellerId: "",
  });

  const normalizeUserTypeForForm = (type) => {
    return type;
  };

  const mapUserTypeForApi = (type) => {
    return type;
  };

  const sellerOptions = sellers.map((s) => ({
    label: s.businessName || s.sellerName || s.name || s.id,
    value: s.id,
  }));

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    const fetchSellers = async () => {
      setSellersLoading(true);
      try {
        const response = await sellerService.getAll();
        const raw = response?.data ?? response;
        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.content)
            ? raw.content
            : [];
        setSellers(list);
      } catch {
        toast.error("Failed to load sellers");
      } finally {
        setSellersLoading(false);
      }
    };

    fetchSellers();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      setLoading(true);
      fetchUserById(id)
        .then((data) => {
          setForm({
            username: data.username || "",
            password: "",
            userType: normalizeUserTypeForForm(data.userType),
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phoneNumber: data.phoneNumber || "",
            gender: data.gender || "MALE",
            dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
            roleId: data.roles?.[0]?.id || "",
            sellerId: data.sellerId || data.raw?.sellerId || "",
          });
        })
        .catch(() => toast.error("Failed to load user"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  useEffect(() => {
    if (form.userType !== "SELLER_USER") {
      setSellerSearch("");
      return;
    }

    if (!form.sellerId) return;

    const selectedSeller = sellerOptions.find((s) => String(s.value) === String(form.sellerId));
    if (selectedSeller) {
      setSellerSearch(selectedSeller.label);
    }
  }, [form.userType, form.sellerId, sellers]);

  const updateField = (key, value) => {
    setForm((prev) => {
      if (key === "userType" && value !== "SELLER_USER") {
        return { ...prev, [key]: value, sellerId: "" };
      }
      return { ...prev, [key]: value };
    });
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSellerSearchChange = (value) => {
    setSellerSearch(value);
    const matched = sellerOptions.find(
      (s) => s.label.toLowerCase() === value.trim().toLowerCase()
    );
    updateField("sellerId", matched ? matched.value : "");
  };

  const fillTestData = () => {
    const r = Math.floor(Math.random() * 9000) + 1000;
    setForm({
      username: `testuser${r}`,
      password: 'Test@1234',
      userType: 'MANAGEMENT',
      firstName: `Test${r}`,
      lastName: 'User',
      email: `testuser${r}@test.com`,
      phoneNumber: `98765${String(r).padStart(5, '0')}`,
      gender: 'MALE',
      dateOfBirth: '1995-06-15',
      roleId: form.roleId,
      sellerId: '',
    });
    setErrors({});
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = "First name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    if (!form.phoneNumber.trim()) errs.phoneNumber = "Phone number is required";
    if (isAdd && !form.username.trim()) errs.username = "Username is required";
    if (isAdd && !form.password.trim()) errs.password = "Password is required";
    if (!form.userType) errs.userType = "User type is required";
    if (form.userType === "SELLER_USER" && !form.sellerId) errs.sellerId = "Seller is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the errors");
      return;
    }

    setSaving(true);
    const formatDate = (date) => (date ? `${date}T00:00:00` : null);

    const payload = {
      id: isEdit ? id : undefined,
      username: form.username,
      password: form.password || undefined,
      userType: mapUserTypeForApi(form.userType),
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      phoneNumber: form.phoneNumber,
      dateOfBirth: formatDate(form.dateOfBirth),
      gender: form.gender,
      roles: [{ id: form.roleId }],
      active: true,
      ...(form.userType === "SELLER_USER" && form.sellerId ? { sellerId: form.sellerId } : {}),
    };

    if (isEdit && !payload.password) delete payload.password;

    try {
      if (isEdit) {
        await updateUser(id, payload);
        toast.success("User updated successfully");
      } else {
        await createUser(payload);
        toast.success("User created successfully");
      }
      navigate("/users");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 to-slate-100">
          <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#C45500] mx-auto" />
            <p className="mt-3 text-slate-600 font-medium">Loading user data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-x-hidden">
        {/* Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 px-4 sm:px-6 py-3 sticky top-0 z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/users")}
                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-200"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-slate-900">
                    {isEdit ? "Edit User" : "New User"}
                  </h1>
                  {isEdit && form.firstName && (
                    <span className="px-3 py-1 bg-[#FFE8C8] text-[#C45500] text-sm font-medium rounded-full">
                      {form.firstName} {form.lastName}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {isEdit ? `Updating profile for ${form.firstName || "user"}` : "Add a new team member to your organization"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isAdd && (
                <button
                  type="button"
                  onClick={fillTestData}
                  className="px-3 py-2 text-[11px] font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-colors uppercase tracking-wider"
                >
                  Test Data
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate("/users")}
                className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 hover:bg-slate-50 text-sm font-medium transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-5 py-2.5 bg-gradient-to-b from-[#f7ca00] to-[#f0c14b] border border-[#a88734] text-[#0F1111] rounded-xl hover:from-[#f0c14b] hover:to-[#e7ae00] flex items-center gap-2 text-sm font-semibold disabled:opacity-50 transition-all duration-200 shadow-sm"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {isEdit ? "Update User" : "Create User"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-6 py-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <FormCard icon={User} title="Personal Information" subtitle="Basic details about the user">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <FormInput
                    icon={User} label="First Name" required error={errors.firstName}
                    value={form.firstName} onChange={(e) => updateField("firstName", e.target.value)}
                    placeholder="Enter first name"
                  />
                  <FormInput
                    icon={User} label="Last Name"
                    value={form.lastName} onChange={(e) => updateField("lastName", e.target.value)}
                    placeholder="Enter last name"
                  />
                  <FormInput
                    icon={Mail} label="Email" type="email" required error={errors.email}
                    value={form.email} onChange={(e) => updateField("email", e.target.value)}
                    placeholder="email@example.com"
                  />
                  <FormInput
                    icon={Phone} label="Phone Number" required error={errors.phoneNumber}
                    value={form.phoneNumber} onChange={(e) => updateField("phoneNumber", e.target.value)}
                    placeholder="10-digit number"
                  />
                  <FormSelect
                    icon={User} label="Gender"
                    value={form.gender} onChange={(e) => updateField("gender", e.target.value)}
                    options={[
                      { label: "Male", value: "MALE" },
                      { label: "Female", value: "FEMALE" },
                      { label: "Other", value: "OTHER" },
                    ]}
                  />
                  <FormInput
                    icon={Calendar} label="Date of Birth" type="date"
                    value={form.dateOfBirth} onChange={(e) => updateField("dateOfBirth", e.target.value)}
                  />
                </div>
              </FormCard>

              {/* Auth & Access */}
              <FormCard icon={ShieldCheck} title="Authentication & Access" subtitle="Login credentials and role assignment">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <FormInput
                    icon={User} label="Username" required error={errors.username} disabled={isEdit}
                    value={form.username} onChange={(e) => updateField("username", e.target.value)}
                    placeholder="johndoe"
                  />
                  <FormInput
                    icon={Lock} label={isEdit ? "Password (leave blank to keep)" : "Password"}
                    type="password" required={isAdd} error={errors.password}
                    value={form.password} onChange={(e) => updateField("password", e.target.value)}
                    placeholder={isEdit ? "Leave blank to keep current" : "Enter password"}
                  />
                  <FormSelect
                    icon={ShieldCheck} label="User Type" required
                    error={errors.userType}
                    value={form.userType} onChange={(e) => updateField("userType", e.target.value)}
                    options={[
                      { label: "Admin", value: "ADMIN" },
                      { label: "Management", value: "MANAGEMENT" },
                      { label: "Account Manager", value: "ACCOUNT_MANAGER" },
                      { label: "BDE", value: "BDE" },
                      { label: "Team Lead", value: "TEAM_LEAD" },
                      { label: "Branch Manager", value: "BRANCH_MANAGER" },
                      { label: "Seller User", value: "SELLER_USER" },
                    ]}
                  />

                  {form.userType === "SELLER_USER" && (
                    <SearchableSelect
                      icon={Store}
                      label="Select Seller"
                      required
                      error={errors.sellerId}
                      value={form.sellerId}
                      onChange={(val) => updateField("sellerId", val)}
                      options={sellerOptions}
                      placeholder={sellersLoading ? "Loading sellers..." : "Search seller..."}
                      disabled={sellersLoading}
                    />
                  )}
                </div>
              </FormCard>
            </form>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
